import type { ICellRendererParams } from "ag-grid-community";
import type { ReactNode } from "react";

import { EmptyCell } from "./Cells/EmptyCell";

/**
 * True when a cell value carries no information: null, undefined, an empty
 * string, or an empty array. A real `0` or `false` is NOT empty.
 */
export function isEmptyValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Wrap a cell renderer with the shared em-dash fallback: an empty value
 * ({@link isEmptyValue}) renders the {@link EmptyCell}, and a present value
 * renders via `render`. This is the generic behind {@link renderDashOrValue};
 * use it for any custom cell that wants the same empty treatment, e.g.
 *
 *   cellRenderer: renderDashOr((p) => <SomeLink id={p.value} />)
 */
export function renderDashOr<TData = unknown, TValue = unknown>(
  render: (params: ICellRendererParams<TData, TValue>) => ReactNode,
) {
  return function DashOr(params: ICellRendererParams<TData, TValue>) {
    return isEmptyValue(params.value) ? <EmptyCell /> : render(params);
  };
}

/**
 * AG Grid `cellRenderer` that renders an empty value as the shared
 * {@link EmptyCell}, and otherwise the column's formatted value. Keyed off the
 * raw value, so a column's own `valueFormatter` still formats present values.
 */
export const renderDashOrValue = renderDashOr(
  (params: ICellRendererParams) =>
    params.valueFormatted ?? String(params.value),
);
